import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import FeedbackRoundedIcon from '@mui/icons-material/FeedbackRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import api from '../services/api';
import useProfileOverview from '../hooks/useProfileOverview';
import { marketingPalette, marketingSymbols } from '../content/marketing';
import {
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../components/common/PublicPageFrame';
import { getUserProfileUrl } from '../utils/mediaUrl';
import {
  startOAuthLink,
  unlinkOAuthProvider,
  updateStoredUser,
} from '../services/auth';
import useBillingVisibility from '../hooks/useBillingVisibility';
import useEntitlements from '../hooks/useEntitlements';
import { shouldDisplayCreditWallet } from '../utils/billingVisibility';
import FeedbackDialog from '../components/Feedback/FeedbackDialog';

const palette = marketingPalette;

const providerMeta = {
  google: {
    label: 'Google',
    description: 'Use your Google account to sign in.',
  },
  microsoft: {
    label: 'Microsoft',
    description: 'Use your Microsoft account to sign in.',
  },
};

const surfaceSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: { xs: '26px', md: '30px' },
  border: `1px solid ${alpha(palette.ink, 0.08)}`,
  bgcolor: alpha('#ffffff', 0.94),
  boxShadow: '0 28px 72px rgba(18, 61, 103, 0.08)',
};

const insetSurfaceSx = {
  borderRadius: '22px',
  border: `1px solid ${alpha(palette.ink, 0.08)}`,
  bgcolor: alpha(palette.heroBlueMist, 0.56),
};

const tabSx = {
  minHeight: 52,
  px: { xs: 1.5, md: 2.2 },
  color: palette.inkSoft,
  textTransform: 'none',
  fontWeight: 800,
  fontFamily: publicBodyFontFamily,
  '&.Mui-selected': {
    color: palette.heroBlueDeep,
  },
};

const metricTileSx = {
  height: '100%',
  p: 2.2,
  borderRadius: '22px',
  bgcolor: alpha('#ffffff', 0.12),
  border: `1px solid ${alpha('#ffffff', 0.14)}`,
};

function formatDateTime(value) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMinutes(totalMinutes) {
  const numeric = Number(totalMinutes);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return '0 min';
  }

  if (numeric < 60) {
    return `${Math.round(numeric)} min`;
  }

  const hours = Math.floor(numeric / 60);
  const minutes = Math.round(numeric % 60);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

function formatProviderLabel(provider) {
  if (!provider || provider === 'password') {
    return 'Password';
  }

  if (providerMeta[provider]?.label) {
    return providerMeta[provider].label;
  }

  return provider
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function getInitials(firstName, lastName) {
  const first = firstName?.trim?.()?.charAt(0)?.toUpperCase?.() || '';
  const last = lastName?.trim?.()?.charAt(0)?.toUpperCase?.() || '';
  return `${first}${last}` || 'VM';
}

function SectionCard({ eyebrow, title, description, action, children }) {
  return (
    <Box sx={{ ...surfaceSx, p: { xs: 2.4, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ md: 'flex-start' }}
        sx={{ mb: 2.4 }}
      >
        <Box sx={{ maxWidth: 760 }}>
          {eyebrow ? (
            <Typography
              sx={{
                mb: 0.75,
                color: palette.heroBlueMid,
                fontSize: '0.78rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontFamily: publicBodyFontFamily,
              }}
            >
              {eyebrow}
            </Typography>
          ) : null}
          <Typography
            sx={{
              color: palette.ink,
              fontSize: { xs: '1.35rem', md: '1.55rem' },
              lineHeight: 1.08,
              letterSpacing: '-0.03em',
              fontFamily: publicHeadingFontFamily,
            }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography
              sx={{
                mt: 0.9,
                color: palette.inkSoft,
                lineHeight: 1.72,
                fontFamily: publicBodyFontFamily,
              }}
            >
              {description}
            </Typography>
          ) : null}
        </Box>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Stack>
      {children}
    </Box>
  );
}

function MetricTile({ label, value, helper }) {
  return (
    <Box sx={metricTileSx}>
      <Typography sx={{ color: alpha('#ffffff', 0.74), fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
        {label}
      </Typography>
      <Typography sx={{ mt: 0.8, color: palette.white, fontSize: { xs: '1.5rem', md: '1.8rem' }, lineHeight: 1.05, letterSpacing: '-0.04em', fontFamily: publicHeadingFontFamily, wordBreak: 'break-word' }}>
        {value}
      </Typography>
      {helper ? (
        <Typography sx={{ mt: 0.65, color: alpha('#ffffff', 0.7), lineHeight: 1.55, fontFamily: publicBodyFontFamily }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

function DetailLine({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="flex-start">
      <Box sx={{ width: 38, height: 38, flexShrink: 0, borderRadius: '14px', display: 'grid', placeItems: 'center', bgcolor: alpha(palette.heroBlueMid, 0.10), color: palette.heroBlueDeep }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
          {label}
        </Typography>
        <Typography sx={{ mt: 0.3, color: palette.ink, lineHeight: 1.6, wordBreak: 'break-word', fontFamily: publicBodyFontFamily }}>
          {value || 'Not provided'}
        </Typography>
      </Box>
    </Stack>
  );
}

function EmptyMessage({ message }) {
  return (
    <Box sx={{ ...insetSurfaceSx, p: 2.4 }}>
      <Typography sx={{ color: palette.inkSoft, lineHeight: 1.72, fontFamily: publicBodyFontFamily }}>
        {message}
      </Typography>
    </Box>
  );
}

function ProfilePage() {
  const navigate = useNavigate();
  const { overview, setOverview, loading, error, refresh } = useProfileOverview();
  const { shouldShowBilling } = useBillingVisibility();
  const { status: entitlementStatus, loading: entitlementLoading } = useEntitlements({ enabled: true });
  const [activeTab, setActiveTab] = useState('overview');
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    institution: '',
    bio: '',
    phone: '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [linkingProviders, setLinkingProviders] = useState({ google: false, microsoft: false });
  const [unlinkingProviders, setUnlinkingProviders] = useState({ google: false, microsoft: false });

  useEffect(() => {
    if (!overview?.basicInfo) {
      return;
    }

    setProfileForm({
      firstName: overview.basicInfo.firstName || '',
      lastName: overview.basicInfo.lastName || '',
      institution: overview.basicInfo.institution || '',
      bio: overview.basicInfo.bio || '',
      phone: overview.basicInfo.phone || '',
    });
  }, [overview]);

  useEffect(() => {
    if (!overview?.basicInfo || !overview?.account) {
      return;
    }

    updateStoredUser({
      firstName: overview.basicInfo.firstName,
      lastName: overview.basicInfo.lastName,
      institution: overview.basicInfo.institution,
      accountType: overview.basicInfo.accountType,
      universityId: overview.basicInfo.universityId,
      role: overview.account.role,
      legacyRole: overview.account.legacyRole || overview.account.role,
      primaryAuthProvider: overview.account.primaryAuthProvider,
      lastLoginProvider: overview.audit?.lastLoginProvider || null,
    });
  }, [overview]);

  const basicInfo = overview?.basicInfo || {};
  const account = overview?.account || {};
  const usage = overview?.usage || null;
  const billing = overview?.billing || null;
  const cohorts = Array.isArray(overview?.cohorts) ? overview.cohorts : [];
  const linkedProviders = useMemo(
    () => (Array.isArray(account.linkedProviders) ? account.linkedProviders : []),
    [account.linkedProviders]
  );
  const linkedProvidersSet = useMemo(
    () => new Set(linkedProviders.map((provider) => provider.provider)),
    [linkedProviders]
  );
  const isUniversityAccount = basicInfo.accountType === 'university';
  const showCreditBilling = shouldDisplayCreditWallet({
    accountType: basicInfo.accountType,
    entitlementLoading,
    requiresTokens: entitlementStatus?.requiresTokens,
    shouldShowBilling,
  });
  const primaryAuthProvider = account.primaryAuthProvider || 'password';
  const primaryAuthLabel = formatProviderLabel(primaryAuthProvider);
  const profilePictureUrl = basicInfo.profilePictureUrl || getUserProfileUrl(basicInfo.profilePicture);
  const fullName = `${basicInfo.firstName || ''} ${basicInfo.lastName || ''}`.trim() || 'Virtual Medic user';
  const initials = getInitials(basicInfo.firstName, basicInfo.lastName);
  const walletBalance = Number(billing?.wallet?.balance ?? 0);
  const caseCost = Number(billing?.billingSettings?.historyCostTokens ?? 0);
  const casesRemaining = caseCost > 0 ? Math.floor(walletBalance / caseCost) : null;
  const licenseSummary = overview?.licenses?.summary || null;

  const heroMetrics = useMemo(() => {
    if (isUniversityAccount) {
      return [
        { label: 'Cohorts', value: String(cohorts.length), helper: cohorts.length === 1 ? 'Assigned group' : 'Assigned groups' },
        { label: 'Active licences', value: licenseSummary ? `${licenseSummary.activeLicenses}/${licenseSummary.totalLicenses}` : 'Not set', helper: licenseSummary ? 'Current allocation' : 'Awaiting licence data' },
        { label: 'Primary sign-in', value: primaryAuthLabel, helper: 'Current login method' },
      ];
    }

    return [
      { label: 'Completed cases', value: String(usage?.totalSessions || 0), helper: 'Marked consultations' },
      { label: 'Average score', value: usage?.averageScore !== null && usage?.averageScore !== undefined ? `${Math.round(usage.averageScore)}%` : 'Not set', helper: 'Across completed cases' },
      { label: 'Practice time', value: formatMinutes(usage?.totalMinutes), helper: 'Recorded consultation time' },
    ];
  }, [cohorts.length, isUniversityAccount, licenseSummary, primaryAuthLabel, usage]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((current) => ({ ...current, open: false }));
  };

  const syncOverview = (nextOverview) => {
    if (nextOverview) {
      setOverview(nextOverview);
    }
  };

  const handleProfileUpdate = async (event) => {
    event.preventDefault();
    setUpdatingProfile(true);

    try {
      const response = await api.put('/users/profile', {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        institution: profileForm.institution,
        bio: profileForm.bio,
        phone: profileForm.phone,
      });
      syncOverview(response.data?.profile);
      showSnackbar('Profile updated successfully.');
    } catch (updateError) {
      console.error('Failed to update profile', updateError);
      showSnackbar(updateError.response?.data?.message || 'Failed to update profile.', 'error');
    } finally {
      setUpdatingProfile(false);
    }
  };

  const validatePasswordForm = () => {
    const nextErrors = {};

    if (!passwordData.currentPassword) nextErrors.currentPassword = 'Current password is required';
    if (!passwordData.newPassword) nextErrors.newPassword = 'New password is required';
    else if (passwordData.newPassword.length < 8) nextErrors.newPassword = 'New password must be at least 8 characters';
    if (!passwordData.confirmPassword) nextErrors.confirmPassword = 'Please confirm your new password';
    else if (passwordData.confirmPassword !== passwordData.newPassword) nextErrors.confirmPassword = 'New passwords do not match';

    setPasswordErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();
    if (!validatePasswordForm()) return;

    setUpdatingPassword(true);
    try {
      await api.put('/users/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
      showSnackbar('Password updated successfully.');
    } catch (updateError) {
      console.error('Password update error', updateError);
      if (updateError.response?.status === 401) showSnackbar('Current password is incorrect.', 'error');
      else showSnackbar(updateError.response?.data?.message || 'Failed to update password.', 'error');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handlePasswordFieldChange = (field, value) => {
    setPasswordData((current) => ({ ...current, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors((current) => ({ ...current, [field]: '' }));
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('File size must be less than 5 MB.', 'error');
      return;
    }

    setUploadingPicture(true);
    const formData = new FormData();
    formData.append('profilePicture', file);

    try {
      const response = await api.post('/users/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      syncOverview(response.data?.profile);
      showSnackbar('Profile picture updated successfully.');
    } catch (uploadError) {
      console.error('Failed to upload profile picture', uploadError);
      showSnackbar(uploadError.response?.data?.message || 'Failed to upload profile picture.', 'error');
    } finally {
      setUploadingPicture(false);
      event.target.value = '';
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (!window.confirm('Delete your profile picture?')) return;

    try {
      const response = await api.delete('/users/profile-picture');
      syncOverview(response.data?.profile);
      showSnackbar('Profile picture removed.');
    } catch (deleteError) {
      console.error('Failed to delete profile picture', deleteError);
      showSnackbar(deleteError.response?.data?.message || 'Failed to delete profile picture.', 'error');
    }
  };

  const handleLinkProvider = async (provider) => {
    setLinkingProviders((current) => ({ ...current, [provider]: true }));
    try {
      const result = await startOAuthLink(provider);
      if (result.status === 'linked') {
        await refresh();
        showSnackbar(`${providerMeta[provider].label} linked successfully.`);
      } else if (result.status === 'error') {
        showSnackbar(result.message || `Unable to link ${providerMeta[provider].label}.`, 'error');
      }
    } catch (linkError) {
      console.error('Link provider error', linkError);
      showSnackbar(linkError.message || `Unable to link ${providerMeta[provider].label}.`, 'error');
    } finally {
      setLinkingProviders((current) => ({ ...current, [provider]: false }));
    }
  };

  const handleUnlinkProvider = async (provider) => {
    const label = providerMeta[provider].label;
    if (!window.confirm(`Remove ${label} sign-in from your account?`)) return;

    setUnlinkingProviders((current) => ({ ...current, [provider]: true }));
    try {
      await unlinkOAuthProvider(provider);
      await refresh();
      showSnackbar(`${label} removed.`);
    } catch (unlinkError) {
      console.error('Unlink provider error', unlinkError);
      showSnackbar(unlinkError.response?.data?.message || `Unable to unlink ${label}.`, 'error');
    } finally {
      setUnlinkingProviders((current) => ({ ...current, [provider]: false }));
    }
  };

  const renderOverview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={8}>
        <Stack spacing={3}>
          <SectionCard
            eyebrow="Overview"
            title="Profile snapshot"
            description="Your account details, academic context, and recent activity in one place."
            action={(
              <Chip
                label={isUniversityAccount ? 'University account' : 'Individual account'}
                sx={{
                  bgcolor: alpha(palette.heroBlueMid, 0.10),
                  color: palette.heroBlueDeep,
                  borderRadius: '999px',
                  fontWeight: 800,
                  fontFamily: publicBodyFontFamily,
                }}
              />
            )}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Stack spacing={2.2} alignItems={{ xs: 'flex-start', sm: 'center', md: 'flex-start' }}>
                  <Avatar
                    src={profilePictureUrl}
                    sx={{
                      width: 108,
                      height: 108,
                      bgcolor: palette.heroBlueDeep,
                      color: palette.white,
                      fontSize: '2.2rem',
                      fontWeight: 800,
                      fontFamily: publicHeadingFontFamily,
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box>
                    <Typography sx={{ color: palette.ink, fontSize: { xs: '1.5rem', md: '1.8rem' }, lineHeight: 1.08, letterSpacing: '-0.03em', fontFamily: publicHeadingFontFamily }}>
                      {fullName}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.2 }}>
                      <Chip
                        icon={<BadgeRoundedIcon sx={{ color: 'inherit !important' }} />}
                        label={account.legacyRole || account.role || 'Student'}
                        sx={{ bgcolor: alpha(palette.heroBlue, 0.10), color: palette.heroBlueDeep, borderRadius: '999px', fontWeight: 700, fontFamily: publicBodyFontFamily }}
                      />
                      <Chip
                        icon={<LoginRoundedIcon sx={{ color: 'inherit !important' }} />}
                        label={`Primary sign-in: ${primaryAuthLabel}`}
                        sx={{ bgcolor: alpha(palette.heroBlue, 0.10), color: palette.heroBlueDeep, borderRadius: '999px', fontWeight: 700, fontFamily: publicBodyFontFamily }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={8}>
                <Grid container spacing={2.2}>
                  <Grid item xs={12} sm={6}>
                    <DetailLine icon={<EmailRoundedIcon fontSize="small" />} label="Email" value={basicInfo.email} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailLine icon={<SchoolRoundedIcon fontSize="small" />} label="Institution" value={basicInfo.institution || (isUniversityAccount ? 'Assigned by your university' : 'Not provided')} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailLine icon={<LoginRoundedIcon fontSize="small" />} label="Last login" value={formatDateTime(overview?.audit?.lastLoginAt)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailLine icon={<TimerRoundedIcon fontSize="small" />} label="Profile updated" value={formatDateTime(overview?.audit?.profileUpdatedAt)} />
                  </Grid>
                </Grid>

                {basicInfo.bio ? (
                  <Box sx={{ ...insetSurfaceSx, p: 2.1, mt: 2.4 }}>
                    <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                      Bio
                    </Typography>
                    <Typography sx={{ mt: 0.75, color: palette.ink, lineHeight: 1.75, fontFamily: publicBodyFontFamily }}>
                      {basicInfo.bio}
                    </Typography>
                  </Box>
                ) : null}
              </Grid>
            </Grid>
          </SectionCard>

          {isUniversityAccount ? (
            <>
              <SectionCard
                eyebrow="Academic"
                title="Cohorts"
                description="Groups, teaching structure, and coaching details linked to your account."
              >
                {cohorts.length ? (
                  <Stack spacing={2}>
                    {cohorts.map((cohort) => (
                      <Box key={cohort.id} sx={{ ...insetSurfaceSx, p: 2.25 }}>
                        <Stack spacing={1.4}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                            <Typography sx={{ color: palette.ink, fontWeight: 800, fontSize: '1.05rem', fontFamily: publicBodyFontFamily }}>
                              {cohort.name}
                            </Typography>
                            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                              {(cohort.tags || []).slice(0, 3).map((tag) => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  sx={{ bgcolor: alpha(palette.heroBlue, 0.10), color: palette.heroBlueDeep, fontWeight: 700, fontFamily: publicBodyFontFamily }}
                                />
                              ))}
                            </Stack>
                          </Stack>
                          {cohort.description ? (
                            <Typography sx={{ color: palette.inkSoft, lineHeight: 1.68, fontFamily: publicBodyFontFamily }}>
                              {cohort.description}
                            </Typography>
                          ) : null}
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <DetailLine icon={<GroupsRoundedIcon fontSize="small" />} label="Coach" value={cohort.coach?.name || overview?.tenant?.contact?.name || 'Assigned shortly'} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <DetailLine icon={<TimerRoundedIcon fontSize="small" />} label="Schedule" value={cohort.schedule?.startDate ? `Starts ${formatDateTime(cohort.schedule.startDate)}` : 'To be confirmed'} />
                            </Grid>
                          </Grid>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <EmptyMessage message="You are not currently assigned to a cohort. Your administrator can add one later." />
                )}
              </SectionCard>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <SectionCard
                    eyebrow="Academic"
                    title="Licence usage"
                    description="Seat allocation and active licence availability for your institution."
                  >
                    {licenseSummary ? (
                      <Stack spacing={2}>
                        <Box sx={{ ...insetSurfaceSx, p: 2.1 }}>
                          <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                            Seats allocated
                          </Typography>
                          <Typography sx={{ mt: 0.8, color: palette.ink, fontSize: { xs: '1.5rem', md: '1.75rem' }, letterSpacing: '-0.03em', fontFamily: publicHeadingFontFamily }}>
                            {licenseSummary.seatsAllocated} / {licenseSummary.seatLimit || 'Unlimited'}
                          </Typography>
                        </Box>
                        <DetailLine icon={<BadgeRoundedIcon fontSize="small" />} label="Active licences" value={`${licenseSummary.activeLicenses} of ${licenseSummary.totalLicenses}`} />
                        <DetailLine icon={<TimerRoundedIcon fontSize="small" />} label="Nearest expiry" value={licenseSummary.nearestExpiry ? formatDateTime(licenseSummary.nearestExpiry) : 'Not scheduled'} />
                      </Stack>
                    ) : (
                      <EmptyMessage message="Licence information is not available for this account yet." />
                    )}
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SectionCard
                    eyebrow="Institution"
                    title="University details"
                    description="Organisation details shared with your account and cohort."
                  >
                    {overview?.tenant ? (
                      <Stack spacing={2}>
                        <Box sx={{ ...insetSurfaceSx, p: 2.1 }}>
                          <Typography sx={{ color: palette.ink, fontWeight: 800, fontSize: '1.1rem', fontFamily: publicBodyFontFamily }}>
                            {overview.tenant.name}
                          </Typography>
                          {overview.tenant.logoUrl ? (
                            <Box component="img" src={overview.tenant.logoUrl} alt={overview.tenant.name} sx={{ mt: 1.4, maxWidth: 180, maxHeight: 56, objectFit: 'contain' }} />
                          ) : null}
                        </Box>
                        <DetailLine icon={<EmailRoundedIcon fontSize="small" />} label="Contact" value={overview.tenant.contact?.email || 'Not provided'} />
                      </Stack>
                    ) : (
                      <EmptyMessage message="University details are not available for this account." />
                    )}
                  </SectionCard>
                </Grid>
              </Grid>
            </>
          ) : (
            <Grid container spacing={3}>
              {showCreditBilling ? (
                <Grid item xs={12} md={6}>
                  <SectionCard
                    eyebrow="Billing"
                    title="Credits"
                    description="Your current wallet balance and the cost of completed consultation exports."
                    action={billing?.status ? (
                      <Chip
                        label={String(billing.status).replace(/-/g, ' ')}
                        sx={{ bgcolor: alpha(palette.heroBlueMid, 0.10), color: palette.heroBlueDeep, borderRadius: '999px', fontWeight: 800, textTransform: 'capitalize', fontFamily: publicBodyFontFamily }}
                      />
                    ) : null}
                  >
                    {billing ? (
                      <Stack spacing={2}>
                        <Box sx={{ ...insetSurfaceSx, p: 2.1 }}>
                          <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                            Wallet balance
                          </Typography>
                          <Typography sx={{ mt: 0.8, color: palette.ink, fontSize: { xs: '1.5rem', md: '1.75rem' }, letterSpacing: '-0.03em', fontFamily: publicHeadingFontFamily }}>
                            {walletBalance.toLocaleString()} credits
                          </Typography>
                        </Box>
                        <DetailLine icon={<MonetizationOnRoundedIcon fontSize="small" />} label="Cost per completed case" value={caseCost ? `${caseCost} credits` : 'Not configured'} />
                        <DetailLine icon={<TimerRoundedIcon fontSize="small" />} label="Remaining completed cases" value={casesRemaining !== null ? `${casesRemaining}` : 'Not available'} />
                        <Button onClick={() => navigate('/account/billing')} endIcon={<ArrowOutwardRoundedIcon />} sx={{ ...publicPrimaryButtonSx, minHeight: 46, alignSelf: 'flex-start' }}>
                          Manage billing
                        </Button>
                      </Stack>
                    ) : (
                      <EmptyMessage message="Billing details will appear here once your account has an active wallet." />
                    )}
                  </SectionCard>
                </Grid>
              ) : null}

              <Grid item xs={12} md={showCreditBilling ? 6 : 12}>
                <SectionCard
                  eyebrow="Progress"
                  title="Usage"
                  description="A quick view of your recent consultation activity and score trend."
                >
                  {usage ? (
                    <Stack spacing={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ ...insetSurfaceSx, p: 2.1, height: '100%' }}>
                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                              Sessions
                            </Typography>
                            <Typography sx={{ mt: 0.75, color: palette.ink, fontSize: { xs: '1.5rem', md: '1.7rem' }, letterSpacing: '-0.03em', fontFamily: publicHeadingFontFamily }}>
                              {usage.totalSessions || 0}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ ...insetSurfaceSx, p: 2.1, height: '100%' }}>
                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                              Average score
                            </Typography>
                            <Typography sx={{ mt: 0.75, color: palette.ink, fontSize: { xs: '1.5rem', md: '1.7rem' }, letterSpacing: '-0.03em', fontFamily: publicHeadingFontFamily }}>
                              {usage.averageScore !== null && usage.averageScore !== undefined ? `${Math.round(usage.averageScore)}%` : 'Not set'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ ...insetSurfaceSx, p: 2.1, height: '100%' }}>
                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                              Practice time
                            </Typography>
                            <Typography sx={{ mt: 0.75, color: palette.ink, fontSize: { xs: '1.5rem', md: '1.7rem' }, letterSpacing: '-0.03em', fontFamily: publicHeadingFontFamily }}>
                              {formatMinutes(usage.totalMinutes)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {usage.lastSession ? (
                        <Box sx={{ ...insetSurfaceSx, p: 2.2 }}>
                          <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                            Most recent case
                          </Typography>
                          <Typography sx={{ mt: 0.75, color: palette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                            {usage.lastSession.title}
                          </Typography>
                          <Typography sx={{ mt: 0.55, color: palette.inkSoft, lineHeight: 1.65, fontFamily: publicBodyFontFamily }}>
                            Completed {formatDateTime(usage.lastSession.completedAt)} with a score of {usage.lastSession.overallPercentage}%.
                          </Typography>
                        </Box>
                      ) : null}
                    </Stack>
                  ) : (
                    <EmptyMessage message="Once you complete cases, your usage summary will appear here." />
                  )}
                </SectionCard>
              </Grid>
            </Grid>
          )}
        </Stack>
      </Grid>

      <Grid item xs={12} lg={4}>
        <Stack spacing={3}>
          <SectionCard eyebrow="Support" title="Support links" description="Published resources for your account type and institution.">
            {(overview?.support?.links || []).length ? (
              <Stack spacing={1.5}>
                {overview.support.links.map((linkItem) => (
                  <Box key={linkItem.id} sx={{ ...insetSurfaceSx, p: 2.1 }}>
                    <Typography sx={{ color: palette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                      {linkItem.title}
                    </Typography>
                    <Typography sx={{ mt: 0.7, color: palette.inkSoft, lineHeight: 1.68, fontFamily: publicBodyFontFamily }}>
                      {linkItem.description}
                    </Typography>
                    <Link href={linkItem.url} target="_blank" rel="noopener noreferrer" underline="none" sx={{ display: 'inline-block', mt: 1.3 }}>
                      <Button endIcon={<ArrowOutwardRoundedIcon />} sx={{ ...publicSecondaryButtonSx, minHeight: 42 }}>
                        Open resource
                      </Button>
                    </Link>
                  </Box>
                ))}
              </Stack>
            ) : (
              <EmptyMessage message="Support links will appear here when your institution or administrator publishes them." />
            )}
          </SectionCard>

          <SectionCard eyebrow="Product" title="Feedback" description="Send product feedback directly to the team from your account.">
            <Box sx={{ ...insetSurfaceSx, p: 2.2 }}>
              <Typography sx={{ color: palette.inkSoft, lineHeight: 1.72, fontFamily: publicBodyFontFamily }}>
                Flag issues, request improvements, or share what is working well.
              </Typography>
              <Button onClick={() => setFeedbackOpen(true)} startIcon={<FeedbackRoundedIcon />} sx={{ ...publicPrimaryButtonSx, minHeight: 46, mt: 1.6 }}>
                Send feedback
              </Button>
            </Box>
          </SectionCard>
        </Stack>
      </Grid>
    </Grid>
  );
  const renderAccount = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={4}>
        <SectionCard eyebrow="Account" title="Photo" description="Update how your profile appears across the app.">
          <Stack spacing={2.2} alignItems={{ xs: 'flex-start', sm: 'center', lg: 'flex-start' }}>
            <Avatar
              src={profilePictureUrl}
              sx={{
                width: 132,
                height: 132,
                bgcolor: palette.heroBlueDeep,
                color: palette.white,
                fontSize: '2.8rem',
                fontWeight: 800,
                fontFamily: publicHeadingFontFamily,
              }}
            >
              {initials}
            </Avatar>

            <Typography sx={{ color: palette.inkSoft, lineHeight: 1.7, fontFamily: publicBodyFontFamily }}>
              Upload a square image for the best result. Maximum file size is 5 MB.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row', lg: 'column' }} spacing={1.2} sx={{ width: '100%' }}>
              <input
                accept="image/*"
                id="profile-picture-upload"
                type="file"
                style={{ display: 'none' }}
                onChange={handleProfilePictureUpload}
                disabled={uploadingPicture}
              />
              <label htmlFor="profile-picture-upload">
                <Button
                  component="span"
                  startIcon={uploadingPicture ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <CameraAltRoundedIcon />}
                  disabled={uploadingPicture}
                  sx={{ ...publicPrimaryButtonSx, minHeight: 46, width: '100%' }}
                >
                  {uploadingPicture ? 'Uploading...' : 'Change picture'}
                </Button>
              </label>

              {basicInfo.profilePicture ? (
                <Button onClick={handleDeleteProfilePicture} startIcon={<DeleteOutlineRoundedIcon />} sx={{ ...publicSecondaryButtonSx, minHeight: 46, width: '100%' }}>
                  Remove picture
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </SectionCard>
      </Grid>

      <Grid item xs={12} lg={8}>
        <SectionCard
          eyebrow="Account"
          title="Profile details"
          description="Update your basic information without changing how your account is authenticated."
        >
          <Box component="form" onSubmit={handleProfileUpdate}>
            <Grid container spacing={2.2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="First name"
                  value={profileForm.firstName}
                  onChange={(event) => setProfileForm((current) => ({ ...current, firstName: event.target.value }))}
                  sx={publicTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Last name"
                  value={profileForm.lastName}
                  onChange={(event) => setProfileForm((current) => ({ ...current, lastName: event.target.value }))}
                  sx={publicTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  disabled
                  label="Email"
                  value={basicInfo.email || ''}
                  helperText="Email updates are managed separately from this form."
                  sx={publicTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Institution"
                  value={profileForm.institution}
                  onChange={(event) => setProfileForm((current) => ({ ...current, institution: event.target.value }))}
                  sx={publicTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={profileForm.phone}
                  onChange={(event) => setProfileForm((current) => ({ ...current, phone: event.target.value }))}
                  sx={publicTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Bio"
                  value={profileForm.bio}
                  onChange={(event) => setProfileForm((current) => ({ ...current, bio: event.target.value }))}
                  placeholder="Add a short introduction for colleagues, tutors, or administrators."
                  sx={publicTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                  <Button type="submit" disabled={updatingProfile} sx={{ ...publicPrimaryButtonSx, minHeight: 48 }}>
                    {updatingProfile ? 'Saving...' : 'Save changes'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setProfileForm({
                      firstName: basicInfo.firstName || '',
                      lastName: basicInfo.lastName || '',
                      institution: basicInfo.institution || '',
                      bio: basicInfo.bio || '',
                      phone: basicInfo.phone || '',
                    })}
                    sx={{ ...publicSecondaryButtonSx, minHeight: 48 }}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </SectionCard>
      </Grid>
    </Grid>
  );

  const renderSecurity = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={6}>
        <SectionCard eyebrow="Security" title="Password" description="Change your password without affecting your linked sign-in providers.">
          <Box component="form" onSubmit={handlePasswordUpdate}>
            <Stack spacing={2.2}>
              <TextField fullWidth required type="password" label="Current password" value={passwordData.currentPassword} onChange={(event) => handlePasswordFieldChange('currentPassword', event.target.value)} error={Boolean(passwordErrors.currentPassword)} helperText={passwordErrors.currentPassword} sx={publicTextFieldSx} />
              <TextField fullWidth required type="password" label="New password" value={passwordData.newPassword} onChange={(event) => handlePasswordFieldChange('newPassword', event.target.value)} error={Boolean(passwordErrors.newPassword)} helperText={passwordErrors.newPassword} sx={publicTextFieldSx} />
              <TextField fullWidth required type="password" label="Confirm new password" value={passwordData.confirmPassword} onChange={(event) => handlePasswordFieldChange('confirmPassword', event.target.value)} error={Boolean(passwordErrors.confirmPassword)} helperText={passwordErrors.confirmPassword} sx={publicTextFieldSx} />
              <Button type="submit" disabled={updatingPassword} sx={{ ...publicPrimaryButtonSx, minHeight: 48, alignSelf: 'flex-start' }}>
                {updatingPassword ? 'Updating...' : 'Update password'}
              </Button>
            </Stack>
          </Box>
        </SectionCard>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Stack spacing={3}>
          <SectionCard eyebrow="Security" title="Sign-in providers" description="Manage Google or Microsoft sign-in alongside your existing account access.">
            <Stack spacing={1.6}>
              {Object.keys(providerMeta).map((provider) => {
                const linkedEntry = linkedProviders.find((item) => item.provider === provider);
                const isLinked = linkedProvidersSet.has(provider);
                const isPrimary = primaryAuthProvider === provider;
                const isBusy = linkingProviders[provider] || unlinkingProviders[provider];

                return (
                  <Box key={provider} sx={{ ...insetSurfaceSx, p: 2.1 }}>
                    <Stack spacing={1.25}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                        <Box>
                          <Typography sx={{ color: palette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                            {providerMeta[provider].label}
                          </Typography>
                          <Typography sx={{ mt: 0.5, color: palette.inkSoft, lineHeight: 1.65, fontFamily: publicBodyFontFamily }}>
                            {providerMeta[provider].description}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                          <Chip
                            label={isLinked ? 'Linked' : 'Not linked'}
                            sx={{ bgcolor: alpha(isLinked ? '#16803c' : palette.ink, isLinked ? 0.12 : 0.08), color: isLinked ? '#16803c' : palette.inkSoft, borderRadius: '999px', fontWeight: 700, fontFamily: publicBodyFontFamily }}
                          />
                          {isPrimary ? (
                            <Chip
                              label="Primary"
                              sx={{ bgcolor: alpha(palette.heroBlueMid, 0.10), color: palette.heroBlueDeep, borderRadius: '999px', fontWeight: 700, fontFamily: publicBodyFontFamily }}
                            />
                          ) : null}
                        </Stack>
                      </Stack>

                      {linkedEntry?.lastLoginAt ? (
                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.92rem', fontFamily: publicBodyFontFamily }}>
                          Last used {formatDateTime(linkedEntry.lastLoginAt)}
                        </Typography>
                      ) : null}

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1}>
                        <Button onClick={() => handleLinkProvider(provider)} disabled={isLinked || isBusy} sx={{ ...publicPrimaryButtonSx, minHeight: 44 }}>
                          {linkingProviders[provider] ? 'Linking...' : 'Link account'}
                        </Button>
                        <Button onClick={() => handleUnlinkProvider(provider)} disabled={!isLinked || isPrimary || isBusy} sx={{ ...publicSecondaryButtonSx, minHeight: 44 }}>
                          {unlinkingProviders[provider] ? 'Removing...' : 'Unlink'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </SectionCard>

          <SectionCard eyebrow="Security" title="Account activity" description="Recent authentication and account update details.">
            <Stack spacing={1.5}>
              <DetailLine icon={<LoginRoundedIcon fontSize="small" />} label="Primary sign-in" value={primaryAuthLabel} />
              <Divider sx={{ borderColor: alpha(palette.ink, 0.08) }} />
              <DetailLine icon={<LoginRoundedIcon fontSize="small" />} label="Last login provider" value={formatProviderLabel(overview?.audit?.lastLoginProvider)} />
              <Divider sx={{ borderColor: alpha(palette.ink, 0.08) }} />
              <DetailLine icon={<TimerRoundedIcon fontSize="small" />} label="Last login" value={formatDateTime(overview?.audit?.lastLoginAt)} />
              <Divider sx={{ borderColor: alpha(palette.ink, 0.08) }} />
              <DetailLine icon={<BadgeRoundedIcon fontSize="small" />} label="Profile updated" value={formatDateTime(overview?.audit?.profileUpdatedAt)} />
            </Stack>
          </SectionCard>
        </Stack>
      </Grid>
    </Grid>
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Box sx={{ ...surfaceSx, py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress sx={{ color: palette.heroBlueMid }} />
          <Typography sx={{ color: palette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Loading your profile...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: '20px', border: `1px solid ${alpha('#b42318', 0.12)}` }}>
          <AlertTitle>Unable to load your profile</AlertTitle>
          {error.response?.data?.message || error.message || 'Please try again.'}
        </Alert>
        <Button onClick={refresh} sx={{ ...publicSecondaryButtonSx, minHeight: 46, mt: 2 }}>
          Try again
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mb: 3,
          p: { xs: 3, sm: 3.5, md: 4.5 },
          borderRadius: { xs: '28px', md: '34px' },
          background: 'linear-gradient(140deg, rgba(18, 61, 103, 0.98) 0%, rgba(21, 83, 129, 0.97) 54%, rgba(49, 182, 228, 0.93) 100%)',
          color: palette.white,
          boxShadow: '0 34px 84px rgba(18, 61, 103, 0.16)',
        }}
      >
        <Box component="img" src={marketingSymbols.silhouette} alt="" aria-hidden sx={{ position: 'absolute', right: { xs: -14, md: 20 }, top: { xs: -18, md: -42 }, width: { xs: 160, md: 260 }, opacity: 0.14, pointerEvents: 'none' }} />

        <Grid container spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} lg={7}>
            <Stack spacing={1.3}>
              <Chip
                icon={<PersonRoundedIcon sx={{ color: 'inherit !important' }} />}
                label="Account settings"
                sx={{ alignSelf: 'flex-start', bgcolor: alpha('#ffffff', 0.12), color: palette.white, borderRadius: '999px', fontWeight: 800, fontFamily: publicBodyFontFamily }}
              />
              <Typography component="h1" sx={{ fontFamily: publicHeadingFontFamily, fontSize: { xs: '2.2rem', md: '3rem' }, lineHeight: 1.02, letterSpacing: '-0.04em' }}>
                My profile
              </Typography>
              <Typography sx={{ maxWidth: 720, color: alpha('#ffffff', 0.84), fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.72, fontFamily: publicBodyFontFamily }}>
                Manage your details, account access, and support resources from one place.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.2 }}>
                <Chip label={account.legacyRole || account.role || 'Student'} sx={{ bgcolor: alpha('#ffffff', 0.12), color: palette.white, borderRadius: '999px', fontWeight: 700, fontFamily: publicBodyFontFamily }} />
                <Chip label={isUniversityAccount ? 'University account' : 'Individual account'} sx={{ bgcolor: alpha('#ffffff', 0.12), color: palette.white, borderRadius: '999px', fontWeight: 700, fontFamily: publicBodyFontFamily }} />
                <Chip label={`Primary sign-in: ${primaryAuthLabel}`} sx={{ bgcolor: alpha('#ffffff', 0.12), color: palette.white, borderRadius: '999px', fontWeight: 700, fontFamily: publicBodyFontFamily }} />
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Grid container spacing={1.6}>
              {heroMetrics.map((metric) => (
                <Grid item xs={12} sm={4} key={metric.label}>
                  <MetricTile label={metric.label} value={metric.value} helper={metric.helper} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ ...surfaceSx, p: { xs: 1.1, md: 1.4 }, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ minHeight: 54, '& .MuiTabs-indicator': { height: 3, borderRadius: '999px', bgcolor: palette.heroBlueMid } }}
        >
          <Tab value="overview" label="Overview" icon={<PersonRoundedIcon />} iconPosition="start" sx={tabSx} />
          <Tab value="account" label="Profile details" icon={<BadgeRoundedIcon />} iconPosition="start" sx={tabSx} />
          <Tab value="security" label="Security" icon={<LockRoundedIcon />} iconPosition="start" sx={tabSx} />
        </Tabs>
      </Box>

      {activeTab === 'overview' ? renderOverview() : null}
      {activeTab === 'account' ? renderAccount() : null}
      {activeTab === 'security' ? renderSecurity() : null}

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            borderRadius: '16px',
            border: `1px solid ${alpha(snackbar.severity === 'error' ? '#b42318' : palette.heroBlueMid, 0.16)}`,
            boxShadow: '0 18px 44px rgba(18, 61, 103, 0.14)',
            '& .MuiAlert-message': { fontFamily: publicBodyFontFamily },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Container>
  );
}

export default ProfilePage;
